import { memo } from 'react';
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  Clock,
  Hash,
  FileUp,
  RefreshCw,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useUploadStore } from '@/store/uploadStore';
import type { UploadStatus } from '@/upload/uploadTypes';

interface UploadQueueItemProps {
  id: string;
  style?: React.CSSProperties;
}

/** Format bytes to human-readable string. */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

const STATUS_ICONS: Record<UploadStatus, React.ReactNode> = {
  pending: <Clock className="h-4 w-4 text-gray-400" />,
  hashing: <Hash className="h-4 w-4 animate-pulse text-amber-500" />,
  registering: <Loader2 className="h-4 w-4 animate-spin text-blue-500" />,
  uploading: <FileUp className="h-4 w-4 text-primary-500" />,
  completing: <Loader2 className="h-4 w-4 animate-spin text-primary-600" />,
  completed: <CheckCircle2 className="h-4 w-4 text-green-500" />,
  failed: <AlertCircle className="h-4 w-4 text-red-500" />,
};

function UploadQueueItemInner({ id, style }: UploadQueueItemProps) {
  const { t } = useTranslation();

  // Subscribe to a single item via selector so we only re-render when it changes.
  const item = useUploadStore((s) => s.queue.find((i) => i.id === id));
  const removeItem = useUploadStore((s) => s.removeItem);

  if (!item) return null;

  const { file, status, progress, error } = item;
  const icon = STATUS_ICONS[status];

  return (
    <div
      style={style}
      className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5 dark:border-gray-800"
    >
      {/* Status icon */}
      <div className="flex-shrink-0">{icon}</div>

      {/* File info */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">
          {file.name}
        </p>
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span>{formatBytes(file.size)}</span>
          {status === 'uploading' && (
            <span className="text-primary-600 dark:text-primary-400">
              {progress}%
            </span>
          )}
          {status === 'failed' && error && (
            <span className="text-red-500 dark:text-red-400" title={error}>
              {error.length > 40 ? `${error.slice(0, 40)}...` : error}
            </span>
          )}
          {status === 'hashing' && (
            <span className="text-amber-600 dark:text-amber-400">
              {t('upload.statusHashing')}
            </span>
          )}
          {status === 'registering' && (
            <span className="text-blue-600 dark:text-blue-400">
              {t('upload.statusRegistering')}
            </span>
          )}
          {status === 'completing' && (
            <span className="text-primary-600 dark:text-primary-400">
              {t('upload.statusCompleting')}
            </span>
          )}
        </div>

        {/* Progress bar */}
        {(status === 'uploading' || status === 'completing') && (
          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-shrink-0 items-center gap-1">
        {status === 'failed' && (
          <button
            type="button"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            title={t('actions.retry')}
            onClick={() => {
              // Move this specific item back to pending
              useUploadStore.getState().updateItemStatus(id, 'pending');
            }}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        )}
        {status !== 'uploading' && status !== 'completing' && (
          <button
            type="button"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            title={t('actions.delete')}
            onClick={() => removeItem(id)}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export const UploadQueueItem = memo(UploadQueueItemInner);
